/**
 * Cliente HTTP para a entidade Caso pericial (EW.F1.T06). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/cases.py e apps/api/runtime/cases.py. Cobre: // ux-neutrality:allow doc-comment
 * - createCase: POST /casos com payload do formulário "Etapa 1". // ux-neutrality:allow doc-comment
 * - listCases: GET /casos paginado. // ux-neutrality:allow doc-comment
 * - getCase: GET /casos/:id. // ux-neutrality:allow doc-comment
 *
 * classifyCaseError mapeia HTTP status para razões tipadas que a UI consome // ux-neutrality:allow doc-comment
 * para escolher mensagem i18n (sem leak de detalhe técnico). // ux-neutrality:allow doc-comment
 */
import type { EstadoDaLeituraDoCaso } from '@/lib/conferencia_da_leitura';
import type { EtapaDoFluxo, LiberacaoNoCaso } from '@/lib/conferencia_das_etapas';
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { ArquivoPendenteData } from '@/lib/arquivos_pendentes';

// Contrato ----------------------------------------------------------------

export type CaseStatus = 'draft' | 'analysing' | 'done' | 'conflict';

/**
 * E74.F1 (ADR-0075 §1) — modalidade do caso, escolhida na criação e imutável
 * depois. Decide a mesa e o laudo: `grafotecnica` compara traço (ACE-V);
 * `eletronica` valida a integridade do processo de assinatura eletrônica.
 */
/** E144.F1 (ADR-0098 §2.5): o modo do exame — PARTE por padrão; nunca vem do arquivo. */
export type ModoDoExame = 'parte' | 'juizo';

export type Modalidade = 'grafotecnica' | 'eletronica';
export const MODALIDADE_DEFAULT: Modalidade = 'grafotecnica';

export type ReviewStatus = 'pending_review' | 'approved' | 'rejected';
export type ReviewDecision = 'approved' | 'rejected';

// E23.F24 — escada de status do fluxo (computada no backend).
export type WorkflowStatus =
  | 'rascunho'
  | 'em_analise'
  | 'encerrado_triagem'
  // E72.F1 — encerrado na triagem EMITINDO o Relatório Preliminar. Estado
  // distinto do anterior, e não sinônimo: só ele tem documento para baixar.
  | 'encerrado_com_relatorio'
  | 'laudo_em_elaboracao'
  | 'aguardando_revisor'
  | 'devolvido_pelo_revisor'
  // E78.F1 — raia eletrônica com todas as peças concluídas. Derivado no
  // backend a partir da própria raia (peças vivas × concluídas), não de
  // narrativa aprovada: esta modalidade não produz narrativa.
  | 'eletronica_concluida'
  | 'aguardando_arquivos'
  | 'finalizado'
  // E224.F1 (ADR-0151): requisição entregue (laudo assinado) e nenhuma pendente. Vence a escada.
  | 'assinado'
  // E224.F2 (ADR-0151 §5): a equipe cancelou o caso, com motivo. Vence tudo; não se desfaz.
  | 'cancelado';

/** E224.F2: o cancelamento gravado — quando, por quem (o e-mail) e o motivo. */
export interface CancelamentoDoCasoData {
  em: string;
  por_email: string;
  motivo: string;
}

/** E163.F1 (ADR-0112 §1): `informada` pelo perito ou `cadastro_anterior` (carga da 0150). */
export type OrigemDaDataDoAto = 'informada' | 'cadastro_anterior';

export interface CaseRecordData {
  id: string;
  usuario_id: string;
  escritorio_id: string;
  display_code: string;
  title: string;
  requester: string | null;
  process_number: string | null;
  especime: string | null;
  parties: string | null;
  received_at: string;
  deadline: string | null;
  /** E163.F1 (ADR-0112 §1): a data do ato (assinatura do documento), só quando informada. */
  data_do_ato?: string | null;
  /** De onde veio a data do ato; nula junto com ela. */
  data_do_ato_origem?: OrigemDaDataDoAto | null;
  cpf?: string | null;
  contrato?: string | null;
  /** E88.F1 (GUS-30 RF01/CA01) — o endereço do signatário. */
  endereco?: string | null;
  /** E140.F2 (ADR-0096 §2.5): o estado da leitura do caso; `null` = caso sem ficha. */
  leitura_da_conferencia?: EstadoDaLeituraDoCaso | null;
  /** E147.F1 (ADR-0101 §2.3): a liberação de cada etapa executada; `null` = caso sem a conferência
   * das etapas (grafotécnico, ou antes da E147). */
  liberacao_das_etapas?: Partial<Record<EtapaDoFluxo, LiberacaoNoCaso>> | null;
  /** E193.F1 (GUS-259): o que falta ao caso digital; vazio = nada pendente. */
  arquivos_pendentes?: ArquivoPendenteData[];
  // E58.F3 — achado autoral do perito, fora da comparação formal. Opcional no
  // tipo pela convenção dos campos aditivos; o backend sempre envia.
  informacoes_relevantes?: string | null;
  // E60.F3 — caso puxado para a frente da esteira e da área do certificador.
  prioritario?: boolean;
  prioritario_em?: string | null;
  // E50.F3/F4 — caso do portal: banco contratante (null = caso do perito) e
  // flag de entrada de double check. Aditivos, opcionais no tipo.
  banco_id?: string | null;
  // E126.F1 (ADR-0088) — o processo que agrupa os exames. Aditivo, opcional no tipo.
  processo_id?: string | null;
  entrada_double_check?: boolean;
  status: CaseStatus;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  laudo_downloaded_at?: string | null;
  latest_composite_lr?: number | null;
  // E23.F24 — escada de status do fluxo, computada na listagem (pode faltar em
  // respostas que não agregam narrativas, ex.: get_by_id → a UI cai no fallback).
  workflow_status?: WorkflowStatus | null;
  // E23.F6/F7 — homologação do caso pelo perfil Revisor (ADR-0024). Opcional no
  // tipo (convenção dos campos aditivos, ex. laudo_downloaded_at); o backend
  // sempre envia, e a UI trata ausência/pending_review como "não homologado".
  review_status?: ReviewStatus;
  reviewed_by_usuario_id?: string | null;
  reviewed_at?: string | null;
  review_notes?: string | null;
  reviewed_by_email?: string | null;
  // E63.F1.C05/ADR-0068 — declaração da coincidência: `true` quando quem
  // homologou também elaborou. `null`/ausente = caso não homologado (ou
  // homologado antes da migration 0067, quando nada era medido).
  review_mesmo_autor_producao?: boolean | null;
  // E50.F6 — esteira da plataforma (só preenchidos com `escopo`): nome do
  // escritório-cliente, nº de certificações requeridas e o prazo (D-n).
  // E50.F7: a carteira do BANCO também os preenche (papel de banco na rota).
  escritorio_nome?: string | null;
  n_certificacoes_requeridas?: number;
  certificacao_sla_due_at?: string | null;
  // E50.F7 — carteira do banco: nº de CCBs declaradas do caso.
  n_ccbs?: number;
  // E74.F1 — modalidade do caso (ADR-0075). Aditivo, opcional no tipo; o
  // backend sempre envia. Ausente = grafotécnica (o default do servidor).
  modalidade?: Modalidade;
  /** E224.F2 (ADR-0151 §5): o cancelamento pela equipe; `null`/ausente = caso não cancelado. */
  cancelamento?: CancelamentoDoCasoData | null;
}

/**
 * E50.F6 — escopo da esteira da plataforma em GET /casos: `clientes` = casos
 * do portal; `certificacao_requerida` = esteira de certificação (prazo D-n).
 * Exclusivo do escritório-plataforma (servidor responde 403 aos demais).
 */
export type PlatformEscopo = 'clientes' | 'certificacao_requerida';

export interface CaseReviewPayload {
  decision: ReviewDecision;
  notes?: string | null;
}

/**
 * Resposta do GET /casos/:id/review/precheck (E63.F1.C13, ADR-0068 §2.3): // ux-neutrality:allow doc-comment
 * o que a homologação gravaria se acontecesse agora. // ux-neutrality:allow doc-comment
 */
export interface CaseReviewPrecheckData {
  mesmo_autor_producao: boolean;
}

export interface CaseCreateData {
  /** E126.F1 (ADR-0088) — a pasta do cliente; omitida, o título é o código do exame. */
  title?: string | null;
  requester?: string | null;
  process_number?: string | null;
  especime?: string | null;
  parties?: string | null;
  /** E126.F1 — omitida, é a data do recebimento pela plataforma (hoje, em Brasília). */
  received_at?: string;
  /** E163.F1 (ADR-0112 §1) — a data do ato; `null` limpa na edição. A origem é do servidor. */
  data_do_ato?: string | null;
  deadline?: string | null;
  cpf?: string | null;
  /**
   * E165.F1.T03 (ADR-0114 §1) — o CPF do cliente informado na abertura; vai ao processo, como a
   * pasta e o número. Só na criação e sem `processo_id`.
   */
  cpf_informado?: string | null;
  contrato?: string | null;
  /** E88.F1 (GUS-30 RF01/CA01) — o endereço do signatário. */
  endereco?: string | null;
  /** E74.F1 — omitido = `grafotecnica` (default do servidor). Só na criação. */
  modalidade?: Modalidade;
  /**
   * E126.F1 (ADR-0088) — o exame entra num processo existente e herda a pasta e o
   * número dele (o servidor recusa `title`/`process_number` junto). Só na criação.
   */
  processo_id?: string | null;
  /**
   * E143.F2 (ADR-0097) — só a sessão do banco envia: o escritório vinculado que conduz o
   * processo. Omitido com um vínculo só, o servidor escolhe esse.
   */
  escritorio_id?: string | null;
  /**
   * E177.F1 (ADR-0131) — só a sessão da plataforma envia: o escritório requerente (um escritório
   * cliente) do caso que a operação abre. Sem `processo_id`. O banco sai da leitura da CCB.
   */
  origem_escritorio_id?: string;
}

// E33.F4 — atualização parcial do caso (PATCH /casos/:id). Mesma forma de
// CaseCreateData; o backend aplica só os campos enviados (ver CaseUpdate).
// E74.F1: `modalidade` fica de fora — é escolhida na criação e o servidor
// recusa o campo no PATCH (ADR-0075 §1).
// E165.F1.T03: o CPF informado é do processo (`PATCH /api/processos`), não da edição do caso.
export type CaseUpdateData = Partial<
  Omit<CaseCreateData, 'modalidade' | 'processo_id' | 'cpf_informado' | 'origem_escritorio_id'>
> & {
  /** E58.F3 — só editável (não existe na criação do caso). */
  informacoes_relevantes?: string | null;
};

// Erros tipados -----------------------------------------------------------

export type CaseErrorReason =
  | 'not_found'
  | 'validation'
  | 'network'
  | 'reviewer_required'
  // E126.F2: escritório de dois bancos sem cliente escolhido (409 BANCO_ATIVO_REQUERIDO).
  | 'banco_ativo_requerido'
  // E143.F2 (ADR-0097): o banco abrindo pelo escritório vinculado.
  | 'escritorio_requerido'
  | 'escritorio_nao_vinculado'
  | 'banco_sem_escritorio'
  | 'servico_do_escritorio'
  // E165.F1.T03: o CPF informado sem os verificadores certos (422 CPF_INVALIDO).
  | 'cpf_invalido'
  // E177.F1 (ADR-0131): o escritório requerente que não é (mais) cliente.
  | 'origem_invalida'
  | 'unknown';

export class CaseError extends Error {
  public readonly reason: CaseErrorReason;

  constructor(reason: CaseErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'CaseError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;

function isNetworkError(axiosErr: AxiosError): boolean {
  return axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED';
}

function detailString(axiosErr: AxiosError): string {
  const data = axiosErr.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

// Erros discriminados por par status HTTP + detail do backend.
const CASE_REASON_BY_STATUS_DETAIL: Record<string, CaseErrorReason> = {
  // SELF_REVIEW_FORBIDDEN saiu na E63.F1.C05 (ADR-0068): o backend não recusa
  // mais a homologação do próprio autor — declara.
  [`${HTTP_FORBIDDEN}:REVIEWER_REQUIRED`]: 'reviewer_required',
  // E126.F1 (ADR-0088): CASE_PROCESS_ALREADY_EXISTS saiu — o processo agrupa vários exames.
  [`${HTTP_CONFLICT}:BANCO_ATIVO_REQUERIDO`]: 'banco_ativo_requerido',
  [`${HTTP_CONFLICT}:ESCRITORIO_REQUERIDO`]: 'escritorio_requerido',
  [`${HTTP_CONFLICT}:BANCO_SEM_ESCRITORIO`]: 'banco_sem_escritorio',
  [`${HTTP_FORBIDDEN}:ESCRITORIO_NAO_VINCULADO`]: 'escritorio_nao_vinculado',
  [`${HTTP_UNPROCESSABLE}:SERVICO_INDISPONIVEL_AO_BANCO`]: 'servico_do_escritorio',
  [`${HTTP_UNPROCESSABLE}:CPF_INVALIDO`]: 'cpf_invalido',
  [`${HTTP_UNPROCESSABLE}:ORIGEM_INVALIDA`]: 'origem_invalida',
};

export function classifyCaseError(err: unknown): CaseError {
  if (!axios.isAxiosError(err)) return new CaseError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status;
  const detail = detailString(axiosErr);
  const mapped = CASE_REASON_BY_STATUS_DETAIL[`${httpStatus}:${detail}`];
  if (mapped !== undefined) return new CaseError(mapped);
  if (httpStatus === HTTP_NOT_FOUND) return new CaseError('not_found');
  if (httpStatus === HTTP_UNPROCESSABLE) return new CaseError('validation');
  if (isNetworkError(axiosErr)) return new CaseError('network');
  return new CaseError('unknown');
}

// HTTP --------------------------------------------------------------------

export async function createCase(
  client: AxiosInstance,
  payload: CaseCreateData,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.post<CaseRecordData>('/casos', payload);
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

export interface ListCasesOptions {
  limit?: number;
  offset?: number;
  cpf?: string;
  contrato?: string;
  /** E179.F1: número do processo ou trecho da pasta — os exames do processo voltam juntos. */
  processo?: string;
  /** E50.F6: esteira da plataforma. Com escopo, cpf/contrato não se aplicam. */
  escopo?: PlatformEscopo;
  /** E177.F2 (GUS-253 A2): a lista do perito deixa de fora o rascunho eletrônico sem documento. */
  semRascunhoVazio?: boolean;
}

export async function listCases(
  client: AxiosInstance,
  options?: ListCasesOptions,
): Promise<CaseRecordData[]> {
  const params: Record<string, number | string> = {};
  if (options?.limit !== undefined) params.limit = options.limit;
  if (options?.offset !== undefined) params.offset = options.offset;
  // Os termos de busca vão aparados, e só quando há o que procurar (E9.F3, E179.F1).
  for (const chave of ['cpf', 'contrato', 'processo'] as const) {
    const termo = options?.[chave]?.trim() ?? '';
    if (termo !== '') params[chave] = termo;
  }
  if (options?.escopo !== undefined) params.escopo = options.escopo;
  if (options?.semRascunhoVazio === true) params.sem_rascunho_vazio = 'true';
  try {
    const { data } = await client.get<CaseRecordData[]>('/casos', { params });
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

export interface ExportCasesOptions {
  cpf?: string;
  contrato?: string;
}

/**
 * Baixa o consolidado XLSX dos casos do escritório (E44.F1 / ADR-0055; Status em E44.F2). // ux-neutrality:allow doc-comment
 *
 * GET /casos/export.xlsx — quatro colunas (Pasta/Contrato/Resultado/Status) de // ux-neutrality:allow doc-comment
 * todos os casos do tenant que batem os filtros de CPF/contrato ativos, sem teto // ux-neutrality:allow doc-comment
 * de paginação. Retorna o Blob bruto; o caller dispara o download. // ux-neutrality:allow doc-comment
 */
export async function getCasesExportXlsxBlob(
  client: AxiosInstance,
  options?: ExportCasesOptions,
): Promise<Blob> {
  const params: Record<string, string> = {};
  if (options?.cpf !== undefined && options.cpf.trim() !== '') {
    params.cpf = options.cpf.trim();
  }
  if (options?.contrato !== undefined && options.contrato.trim() !== '') {
    params.contrato = options.contrato.trim();
  }
  try {
    const { data } = await client.get<Blob>('/casos/export.xlsx', {
      params,
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

/** Nome do arquivo baixado: `casos_AAAA-MM-DD.xlsx` (espelha o backend). */
export function casesExportFilename(today: Date): string {
  const iso = today.toISOString().slice(0, 10);
  return `casos_${iso}.xlsx`;
}

/** Dispara o download de um Blob no navegador via âncora temporária. */
export function triggerXlsxDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function getCase(client: AxiosInstance, caseId: string): Promise<CaseRecordData> {
  try {
    const { data } = await client.get<CaseRecordData>(`/casos/${caseId}`);
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

/**
 * Atualiza parcialmente os dados do caso (E33.F4 — voltar/editar no wizard). // ux-neutrality:allow doc-comment
 *
 * PATCH /casos/:id. Só os campos enviados são aplicados; os omitidos // ux-neutrality:allow doc-comment
 * preservam o valor atual. 404 → not_found; 422 → validation. // ux-neutrality:allow doc-comment
 */
export async function updateCase(
  client: AxiosInstance,
  caseId: string,
  payload: CaseUpdateData,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.patch<CaseRecordData>(`/casos/${caseId}`, payload);
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

/**
 * Homologa (aprova/rejeita) o caso — perfil Revisor (E23.F7, ADR-0024). // ux-neutrality:allow doc-comment
 *
 * PATCH /casos/:id/review. Gateado no backend por `reviewer_required` // ux-neutrality:allow doc-comment
 * (owner|revisor); a coincidência entre elaboração e homologação não recusa mais // ux-neutrality:allow doc-comment
 * — volta declarada em review_mesmo_autor_producao (ADR-0068). // ux-neutrality:allow doc-comment
 */
/**
 * Marca ou desmarca o caso como prioritário (E60.F3). // ux-neutrality:allow doc-comment
 *
 * Rota própria, não campo do PATCH de cadastro: priorizar não é corrigir dados, // ux-neutrality:allow doc-comment
 * e de carona num salvamento de formulário a marca mudaria sem ninguém notar. // ux-neutrality:allow doc-comment
 */
export async function setCasePrioridade(
  client: AxiosInstance,
  caseId: string,
  prioritario: boolean,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.patch<CaseRecordData>(`/casos/${caseId}/prioridade`, {
      prioritario,
    });
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

export async function reviewCase(
  client: AxiosInstance,
  caseId: string,
  payload: CaseReviewPayload,
): Promise<CaseRecordData> {
  try {
    const { data } = await client.patch<CaseRecordData>(`/casos/${caseId}/review`, payload);
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}

/**
 * Aviso prévio da homologação (E63.F1.C13, ADR-0068 §2.3). // ux-neutrality:allow doc-comment
 *
 * GET /casos/:id/review/precheck. Devolve o que a homologação GRAVARIA agora: // ux-neutrality:allow doc-comment
 * quem vai decidir também produziu o material? A tela não deriva isso sozinha — // ux-neutrality:allow doc-comment
 * ela só conhece usuario_id (no caso do portal, a gestora cliente), e as outras // ux-neutrality:allow doc-comment
 * duas fontes (análise assinada, narrativa aprovada) vivem no servidor. // ux-neutrality:allow doc-comment
 */
export async function getCaseReviewPrecheck(
  client: AxiosInstance,
  caseId: string,
): Promise<CaseReviewPrecheckData> {
  try {
    const { data } = await client.get<CaseReviewPrecheckData>(`/casos/${caseId}/review/precheck`);
    return data;
  } catch (err) {
    throw classifyCaseError(err);
  }
}
